import React from 'react';
import { useIntl } from 'react-intl';

import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import classnames from 'classnames';
import {
  every,
  filter,
  find,
  get,
  head,
  includes,
  isArray,
  isObject,
  isPlainObject,
  join,
  map,
  orderBy,
  some,
  split,
  toString,
} from 'lodash';
// @ts-ignore
import WebFont from 'webfontloader';

import { Insurance, PatientFormQuestion, EPatientRelationship } from '@lib/resources/types';
import configService from '@lib/services/configService';
import { getQuestionScore } from '@lib/services/patientFormsService';
import storageService from '@lib/services/storageService';

import TextFieldCopy from 'components/TextFieldCopy';
import { Typography as TypographyV2 } from 'components/v2';

import InsuranceCapture from './components/InsuranceCapture/InsuranceCapture';
import InsuranceCard from './components/InsuranceCard/InsuranceCard';
import InsuranceAnswersCopy from './InsuranceAnswersCopy';
import messages from './messages';
import QuestionCopayBillingCopayIdQuery from './QuestionCopayBillingCopayIdQuery';
import QuestionCopayExternalIdQuery from './QuestionCopayExternalIdQuery';

WebFont.load({
  google: {
    families: ['Yellowtail:400', 'sans-serif'],
  },
});

const styles = ({
  custom: {
    palette: { colorSupportPowder, colorSupportSnow },
  },
  palette: { text },
  spacing: { unit },
}: Theme) =>
  createStyles({
    indent: {},
    hidden: {
      display: 'none !important',
    },
    informationCapturePreview: {
      height: 80,
      width: 'auto !important',
    },
    row: {
      display: 'inline-block',
      float: 'left',
      padding: `${unit}px 0px`,
      width: '100%',
      '&$indent': {
        padding: `${unit}px 0px ${unit}px ${unit * 2}px`,
      },
    },
    questionTitle: {
      color: text.primary,
      display: 'inline-block',
      float: 'left',
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '24px',
      paddingBottom: unit / 2,
      width: '100%',
    },
    questionTitleLocale: {
      color: text.secondary,
      fontSize: 14,
      marginBottom: unit + 4,
    },
    subQuestion: {
      color: text.primary,
      display: 'inline-block',
      float: 'left',
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '40px',
      width: '100%',
    },
    subQuestionText: {
      color: text.primary,
      display: 'inline-block',
      float: 'left',
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '40px',
      paddingRight: unit * 2,
      width: '30%',
    },
    signaturePreview: {
      marginTop: unit,
      width: 320,
      height: 80,
    },
    templateContent: {
      display: 'inline-block',
      float: 'left',
      lineHeight: '24px',
      width: '100%',
    },
    signatureTyped: {
      fontFamily: 'Yellowtail, cursive',
    },
    insuranceAnswerContainer: {
      border: `1px solid ${colorSupportPowder}`,
      padding: `${unit * 3}px ${unit * 4}px`,
      margin: `${unit * 4}px 0px`,
      backgroundColor: '#FFFFFF',
    },
    insuranceAnswerContainerNoTopMargin: {
      marginTop: 0,
    },
    insuranceAnswer: {
      paddingLeft: unit * 2,
    },
    memberDetails: {
      margin: `${unit * 3}px 0px ${unit}px 0px`,
    },
    dependentDetails: {
      margin: `${unit * 2}px 0px ${unit}px 0px`,
    },
    answers: {
      paddingLeft: unit * 2,
    },
    insuranceRow: {
      backgroundColor: colorSupportSnow,
      margin: `${-2 * unit}px 0px 0px ${-3 * unit}px`,
      padding: 'unset',
      width: `calc(100% + ${unit * 6}px)`,
    },
    insuranceRowNoTopMargin: {
      marginTop: 0,
    },
    insuranceTitle: {
      marginBottom: unit * 3,
    },
    insuranceAnswers: {
      display: 'flex',
    },
  });

export type Language = 'es-en' | 'en' | 'es';
interface Props extends WithStyles<typeof styles> {
  settings?: {
    language: Language;
    hideUnanswered: boolean;
    hideUnseen: boolean;
  };
  question: PatientFormQuestion;
  nested?: boolean;
  path?: string[];
  patientFormId?: string;
  checkVisibility?: boolean;
  variant?: 'default' | 'lite';
  position?: number;
}

const getLanguageTemplateLocale = (language: Language, question: PatientFormQuestion) => {
  switch (language) {
    case 'en':
    case 'es-en':
      return get(question, 'properties.template', '');
    case 'es':
      return get(question, 'locales.es.properties.template', '');
    default:
      return question.text;
  }
};

const getLanguageLocale = (language: Language, question: PatientFormQuestion) => {
  switch (language) {
    case 'en':
    case 'es-en':
      return question.text;
    case 'es':
      return get(question, 'locales.es.title', question.text);
    default:
      return question.text;
  }
};

const hasAnswer = (question: PatientFormQuestion) =>
  !every(question.answers, (answer) => !answer.text || (answer.text && !answer.selected));

export const isQuestionVisible = (question: PatientFormQuestion): boolean => {
  const visibility = get(question, 'properties.visibility', 'default');
  return (
    visibility === 'visible' ||
    (visibility === 'default' && (hasAnswer(question) || some(question.properties.items, isQuestionVisible)))
  );
};

const isInsuranceObject = (insurance: any): insurance is Insurance => isPlainObject(insurance);

const Question: React.FC<Props> = ({
  classes,
  settings,
  question,
  nested,
  path,
  patientFormId,
  checkVisibility,
  variant = 'default',
  position,
}) => {
  const { formatMessage } = useIntl();
  const pickedLanguage = getLanguageLocale(get(settings, 'language', 'en'), question);

  const questionTextHTML =
    (pickedLanguage || '')
      .replace('10pt', '13px')
      .replace('18pt', '18px')
      .split('<br')[0] || '';
  const score = getQuestionScore(question);

  const renderAnswerField = (text: string, isReason: boolean) => {
    if (isReason && !text) {
      return;
    }
    const multiline = includes(text, '|||');
    const value = multiline
      ? join(
          map(split(text, '|||'), (s) => `• ${s}`),
          '\n\n',
        )
      : text;
    if (variant === 'lite') {
      return <Typography variant="body1">{value}</Typography>;
    }
    return <TextFieldCopy value={value} multiline={multiline} />;
  };

  const renderInsuranceVerificationAnswerField = (answers: Array<[string, string]>, className: string) => {
    const elements = answers.map((answer) => (
      <div>
        <TypographyV2 variant="bW600F16">{answer[0]}:</TypographyV2>{' '}
        <TypographyV2 variant="bW500F16">{answer[1]}</TypographyV2>
      </div>
    ));
    const copyText = join(
      map(answers, (answer) => join(answer, ': ')),
      '\n\n',
    );

    return <InsuranceAnswersCopy textToCopy={copyText}>{elements}</InsuranceAnswersCopy>;
  };

  const getInsuranceVerificationAnswers = (insuranceObject: Partial<Insurance>) => {
    const insuranceAnwer: Record<string, Array<[string, string]>> = {};
    if (insuranceObject.dependent && insuranceObject.dependent.firstname) {
      const { dependent } = insuranceObject;
      const dob = dependent.dateOfBirth;

      insuranceAnwer.dependentDetails = [
        [formatMessage(messages.dependentName), `${dependent.firstname} ${dependent.lastname}`],
        [
          formatMessage(messages.dependentDOB),
          `${`${dob.month}`.padStart(2, '0')}/${`${dob.day}`.padStart(2, '0')}/${`${dob.year}`}`,
        ],
        [formatMessage(messages.relationshipToSubscriber), dependent.relationshipToSubscriber],
        [formatMessage(messages.dependentAddress), dependent.address],
      ];
    }
    insuranceAnwer.memberDetails = [
      [formatMessage(messages.payerName), insuranceObject.payerName ?? ''],
      [formatMessage(messages.memberName), insuranceObject.memberName ?? ''],
      [formatMessage(messages.memberNumber), insuranceObject.memberNumber ?? ''],
      [formatMessage(messages.insuranceGroupName), insuranceObject.insuranceGroupName ?? ''],
      [formatMessage(messages.planName), insuranceObject.planName ?? ''],
    ];

    return insuranceAnwer;
  };

  const renderAnswers = () => {
    const { answers } = question;

    // Map types to match survey monkey format
    let type = question.type as string;
    if (/multiple_choice|dropdown/.test(type) && !get(question, 'properties.allowMultipleSelection')) {
      type = 'single_choice';
    } else if (/text|email|phone|date|ssn|medication_search|pharmacy_search|address/.test(type)) {
      type = 'open_ended';
    }

    switch (type) {
      case 'datetime':
      case 'zipcode':
      case 'demographic':
      case 'open_ended':
        return map(answers, (answer, index) => {
          const { title } = answer;

          const text =
            isObject(answer.text) && !isArray(answer.text)
              ? get(answer.text, 'text', get(answer.text, 'value', ''))
              : answer.text || '';
          const answerText = isArray(text) ? join(text, '|||') : toString(text);

          return (
            <div key={index}>
              <Typography
                className={classnames(classes.subQuestionText, { [classes.hidden]: !title })}
                component="span"
              >
                {toString(title || '')}
              </Typography>
              {renderAnswerField(answerText, false)}
            </div>
          );
        });
      case 'single_choice': {
        const selectedAnswer = find(answers, { selected: true });
        const answerText = toString(get(selectedAnswer, 'text', '') || get(selectedAnswer, 'label', ''));
        const reason = toString(get(find(answers, 'reason'), 'reason', ''));

        if (selectedAnswer) {
          return (
            <div className={classes.subQuestion}>
              {renderAnswerField(answerText, false)}
              {renderAnswerField(reason, true)}
            </div>
          );
        }
        break;
      }
      case 'dropdown':
      case 'yes_no':
      case 'multiple_choice': {
        const selectedAnswers = filter(answers, { selected: true });
        const reason = toString(get(find(answers, 'reason'), 'reason', ''));

        if (selectedAnswers) {
          const answersText =
            map(selectedAnswers, 'text').join('|||') || map(selectedAnswers, 'label').join('|||') || '';

          return (
            <div className={classes.subQuestion}>
              {renderAnswerField(answersText, false)}
              {renderAnswerField(reason, true)}
            </div>
          );
        }
        break;
      }
      case 'information_capture': {
        const value = get(head(answers), 'text');
        if (value) {
          return /base64/i.test(value) ? (
            // WORKAROUND: Making preview of images backwards compatible
            <img className={classes.informationCapturePreview} src={value} alt="preview" />
          ) : (
            <img
              className={classes.informationCapturePreview}
              src={`${configService.getApiBasePath()}/fileUploads/${value}/file?token=${storageService.getToken()}`}
              alt="Preview"
            />
          );
        }
        break;
      }
      case 'e_signature': {
        const signature = get(head(answers), 'text');
        if (signature) {
          if (signature.startsWith('data:image/png')) {
            return <img className={classes.signaturePreview} src={signature} alt="preview" />;
          }
          return (
            <Typography className={classes.signatureTyped} variant="h3">
              {signature}
            </Typography>
          );
        }
        break;
      }
      case 'patient_information':
      case 'demographics':
      case 'imh':
      case 'group': {
        return (
          <div className={classes.subQuestion}>
            {map(question.properties.items, (item) => (
              <Question
                key={item.id}
                settings={settings}
                classes={classes}
                nested
                question={item as PatientFormQuestion}
                path={path}
                checkVisibility={checkVisibility}
              />
            ))}
          </div>
        );
      }
      case 'template': {
        return (
          <div className={classes.templateContent}>
            <Typography
              className={classes.questionTitle}
              component="span"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: getLanguageTemplateLocale(get(settings, 'language', 'en'), question),
              }}
            />
          </div>
        );
      }
      case 'payment': {
        const answer = get(question, 'answers[0].text');
        return <pre>{JSON.stringify(answer, null, 2)}</pre>;
      }
      case 'copay': {
        // For a succesfully answered COPAY question, we are creating a string answer that follows the format:
        //
        // e.g. "17394619T69064209|Mon Mar 01 2021 09:28:49 GMT-0800|250"
        //
        // The first part of this string contains an externalId which will be used to fetch further information
        const answer = get(question, 'answers[0].text', '');
        const externalId = answer.split('|')[0];

        if (externalId) {
          return (
            <QuestionCopayExternalIdQuery
              patientFormId={patientFormId}
              externalId={externalId}
              questionTextHTML={questionTextHTML}
            />
          );
        }

        // For a failed COPAY question, we do not have an externalID.
        // BUT we do have a billingCopayId which we can use to fetch the status of the transaction
        const billingCopayId = get(question, 'properties.copay', null);
        return (
          <QuestionCopayBillingCopayIdQuery
            patientFormId={patientFormId}
            billingCopayId={billingCopayId}
            questionTextHTML={questionTextHTML}
          />
        );
      }
      case 'insurance_capture':
        return <InsuranceCapture question={question} />;
      case 'insurance_card':
        return <InsuranceCard question={question} />;
      case 'insurance_verification': {
        let insuranceObjects: Array<Partial<Insurance>> = [
          {
            payerName: '',
            memberName: '',
            memberNumber: '',
            insuranceGroupName: '',
            planName: '',
            primary: false,
            dependent: {
              address: '',
              dateOfBirth: {
                day: 1,
                month: 1,
                year: 9999,
              },
              firstname: '',
              lastname: '',
              relationshipToSubscriber: EPatientRelationship.Self,
            },
          },
        ];
        const patientAnswers = question.insurances || [];

        // Multi-insurance flow
        if (isArray(patientAnswers) && patientAnswers.length) {
          insuranceObjects = patientAnswers;
        } else if (isInsuranceObject(patientAnswers)) {
          // Backward compatibility - Single insurance flow.
          insuranceObjects = [patientAnswers];
        }

        // Sort insurances, keep primary first
        const sortedInsurances = orderBy(insuranceObjects, ['primary', 'updatedAt'], ['desc', 'desc']);

        return (
          <div className={classes.subQuestion}>
            {sortedInsurances.map((insuranceObject, index) => {
              const answerText = getInsuranceVerificationAnswers(insuranceObject);
              return (
                <div
                  className={classnames(classes.insuranceAnswerContainer, {
                    [classes.insuranceAnswerContainerNoTopMargin]: position === 0,
                  })}
                >
                  {index === 0 && (
                    <TypographyV2 className={classes.insuranceTitle} variant="h2W600F18">
                      {formatMessage(messages.insuranceTitle)}
                    </TypographyV2>
                  )}
                  <div className={classes.insuranceAnswer}>
                    <TypographyV2 variant="bW600F16">
                      {formatMessage(
                        insuranceObject.primary ? messages.primaryInsurance : messages.additionalInsurance,
                      )}
                    </TypographyV2>
                    <div className={classes.answers}>
                      <TypographyV2 className={classes.memberDetails} variant="bW500F16">
                        {formatMessage(messages.memberDetails)}
                      </TypographyV2>
                      {renderInsuranceVerificationAnswerField(answerText.memberDetails, classes.insuranceAnswers)}
                      {answerText.dependentDetails && (
                        <>
                          <TypographyV2 className={classes.dependentDetails} variant="bW500F16">
                            {formatMessage(messages.dependentDetails)}
                          </TypographyV2>
                          {renderInsuranceVerificationAnswerField(
                            answerText.dependentDetails,
                            classes.insuranceAnswers,
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
      // TODO check with team how we want to handle resources question on files tab
      case 'resources': {
        const answerText = (head(answers)?.text as string[] | undefined) ?? [];
        return <div className={classes.subQuestion}>{renderAnswerField(join(answerText, '|||'), false)}</div>;
      }
      // CCDA question types:
      case 'medication_list': {
        const answersText = map(answers, (answer: any) => {
          const { description, dosage, periodicity } = answer;
          const text = [description, dosage, periodicity].filter((item) => item !== undefined).join(', ');

          return text;
        });

        return <div className={classes.subQuestion}>{renderAnswerField(answersText.join('|||'), false)}</div>;
      }
      case 'immunization_history':
      case 'surgical_history': {
        const answersText = map(answers, (answerItem: any) => {
          const { description, friendlyName, answer } = answerItem;
          let formattedAnswer = answer;
          if (answer === `I don't know`) {
            formattedAnswer = 'Unknown date';
          } else if (answer === null) {
            formattedAnswer = 'Never received';
          }
          const text = `${description}${friendlyName ? ` - "${friendlyName}"` : ''}, ${formattedAnswer}`;

          return text;
        });

        return <div className={classes.subQuestion}>{renderAnswerField(answersText.join('|||'), false)}</div>;
      }
      case 'allergy': {
        const answersText = map(answers, (answerItem: any) => {
          const { description, reaction } = answerItem;
          const text = `${description}, ${reaction || 'Unknown reaction'}`;

          return text;
        });

        return <div className={classes.subQuestion}>{renderAnswerField(answersText.join('|||'), false)}</div>;
      }
      case 'family_history': {
        const answersText = map(answers, (answerItem: any) => {
          const { description, friendlyName, familyMembers } = answerItem;
          const formattedFamilyMembers =
            familyMembers && familyMembers.length > 0 ? familyMembers.join(', ') : 'Unknown family member';
          const text = `${description}${friendlyName ? ` - "${friendlyName}"` : ''}, ${formattedFamilyMembers}`;

          return text;
        });

        return <div className={classes.subQuestion}>{renderAnswerField(answersText.join('|||'), false)}</div>;
      }
      case 'social_history': {
        return (
          <div className={classes.subQuestion}>
            {map(question.answers, (answer: any) => (
              <div key={answer.id} className={classnames(classes.row, classes.indent)}>
                <Typography className={classes.questionTitle} component="span">
                  {answer.title}
                </Typography>
                {renderAnswerField(answer.answer, false)}
              </div>
            ))}
          </div>
        );
      }
      default:
        return <div />;
    }
  };

  // always skip jump questions
  if (question.type === 'jump') return null;

  // hide question in case the visibility is hidden or default and there aren't answers to show
  if (checkVisibility && !isQuestionVisible(question)) {
    return null;
  }

  if (get(settings, 'hideUnanswered', false)) {
    // hide the grouped questions in case all items are not answered
    if (get(question, 'properties.items', []).length) {
      const answeredSubQuestions = filter(get(question, 'properties.items', []), hasAnswer);
      if (!answeredSubQuestions.length) return null;
    } else if (
      every(
        question.answers,
        (answer) => includes(path, get(question, 'id', '')) && (!answer.text || (answer.text && !answer.selected)),
      )
    ) {
      return null;
    }
  }

  // in case of grouped questions, hide the entire question
  if (get(settings, 'hideUnseen', false) && !nested && !includes(path, get(question, 'id', ''))) {
    return null;
  }

  const isInsuranceVerification = question.type === 'insurance_verification';
  const isInsuranceVerificationOrInsuranceCapture = isInsuranceVerification || question.type === 'insurance_capture';

  return (
    <div
      key={question.id}
      className={classnames(classes.row, {
        [classes.indent]: nested,
        [classes.insuranceRow]: isInsuranceVerification,
        [classes.insuranceRowNoTopMargin]: isInsuranceVerification && position && position !== 0,
      })}
    >
      {/* We don't know before querying the backend if the copay has an amount or not.
      There is a requiremente where we shouldn't show copays with 0 amount.
      Therefore, we shouldn't show the title preemptively */}
      {question.type !== 'copay' && !isInsuranceVerificationOrInsuranceCapture && (
        <Typography
          className={classes.questionTitle}
          component="span"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: questionTextHTML }}
        />
      )}
      {get(settings, 'language') === 'es-en' && (
        <Typography className={classes.questionTitleLocale}>{get(question, 'locales.es.title', '')}</Typography>
      )}
      {score > 0 && (
        <Typography color="secondary" paragraph>
          Score: {score}
        </Typography>
      )}
      {renderAnswers()}
    </div>
  );
};

export default withStyles(styles)(Question);
